import { LocaleSwitcher } from '@/i18n/LocaleSwitcher';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { PoweredByKycHub } from './PoweredByKycHub';

/**
 * The ONE bottom bar of every capture screen (SPEC_CAPTURE_UI_20260925 §2): every control the
 * representative can tap lives here, sized for a thumb, one gesture per action.
 *
 *   `shoot`  — three slots: [flip] [shutter] [language]   (the caller gives the left and centre slots)
 *   `decide` — one or two wide pills                      (retake · use this / a single CTA)
 *
 * FSD-20 Addendum C: the bar also carries the compact language switcher and the platform credit, which the Shell
 * leaves to it on the camera screens — in shoot mode the switcher is the right slot (Skip moved into the instruction
 * card), in decide mode it sits beside the credit. `lang={false}` empties the right slot (while a clip records).
 * The caption row is for what must stay beside the shutter (the ID step's storage words, the decide states' lines).
 * Test ids are set by the CALLER on the control itself (`take-photo`, `use-this`, `retake`, …) —
 * the two-party probe and the existing tests depend on them staying exactly where they are.
 */
export function CaptureBar({ mode, caption, lang = true, children }: { mode: 'shoot' | 'decide'; caption?: ReactNode; lang?: boolean; children: ReactNode }): JSX.Element {
  const switcher = lang ? <LocaleSwitcher compact /> : null;
  return (
    <footer className={`sv-bar is-${mode}`} data-testid="capture-bar" data-mode={mode}>
      <div className="sv-bar-row">
        {children}
        {mode === 'shoot' ? <BarSlot side="r">{switcher}</BarSlot> : null}
      </div>
      {caption ? <div className="sv-bar-caption">{caption}</div> : null}
      <div className="sv-bar-foot">
        <span />
        <PoweredByKycHub inBar />
        <span className="sv-bar-foot-r">{mode === 'decide' ? switcher : null}</span>
      </div>
    </footer>
  );
}

export function BarSlot({ side, children }: { side: 'l' | 'c' | 'r'; children?: ReactNode }): JSX.Element {
  return <div className={`sv-bar-slot ${side}`}>{children ?? null}</div>;
}

interface ShutterProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** `photo` = a still · `record` = start a clip (the --rec dot) · `stop` = stop the clip (square). */
  kind: 'photo' | 'record' | 'stop';
  /** The accessible name. No visible caption (FSD-20 Addendum C): the ring, the dot and the square say it. */
  label: string;
}

/** The big round shutter. Its words are its accessible name only — the camera gets the caption's height. */
export function Shutter({ kind, label, className, ...rest }: ShutterProps): JSX.Element {
  const cls = ['sv-shutter', `is-${kind}`, className].filter(Boolean).join(' ');
  return (
    <div className="sv-shutter-wrap">
      <button type="button" className={cls} aria-label={label} {...rest}>
        <i />
      </button>
    </div>
  );
}

interface BarButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** The short visible label; pass `aria-label` for the full sentence. */
  label: string;
}

/** A secondary bar control: a 44 px disc with the glyph and a short label under it. */
export function BarButton({ icon, label, className, ...rest }: BarButtonProps): JSX.Element {
  const cls = ['sv-bar-btn', className].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} {...rest}>
      <span className="ic">
        <Icon name={icon} size={20} />
      </span>
      <span>{label}</span>
    </button>
  );
}
